import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core'
import { AddCircleOutline, DeleteOutline } from '@material-ui/icons'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  labelField: {
    flex: 1,
    maxWidth: 200,
  },
  valueField: {
    flex: 2,
  },
  addButton: {
    alignSelf: 'flex-start',
  },
  crmGroup: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  crmLabel: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
}))

const BlurField = ({ value, onCommit, ...props }) => {
  const [local, setLocal] = useState(value || '')
  useEffect(() => { setLocal(value || '') }, [value])
  return (
    <TextField
      {...props}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== (value || '')) onCommit(local) }}
    />
  )
}

const ProjectEditor = ({ data, onChange, crmSnapshot, onCrmChange }) => {
  const classes = useStyles()
  if (data?.flavors?.length) {
    return (
      <Box className={classes.root}>
        <Typography variant="subtitle2">Project Details</Typography>
        <Typography variant="body2" color="textSecondary">
          Plan totals, start-up costs, tax, and contract terms are derived from the Estimate Builder service flavors.
          Update those values in the estimate, then regenerate this proposal.
        </Typography>
      </Box>
    )
  }

  const details = data.details || []

  const handleDetailChange = (index, field, value) => {
    const updated = details.map((detail, i) =>
      i === index ? { ...detail, [field]: value } : detail
    )
    onChange('details', updated)
  }

  const handleAddDetail = () => {
    onChange('details', [...details, { label: '', value: '' }])
  }

  const handleRemoveDetail = (index) => {
    onChange(
      'details',
      details.filter((_, i) => i !== index)
    )
  }

  return (
    <Box className={classes.root}>
      <Typography variant="subtitle2">Project Details</Typography>

      {details.map((detail, index) => (
        <Box key={index} className={classes.detailRow}>
          <BlurField
            label="Label"
            value={detail.label || ''}
            onCommit={(v) =>
              handleDetailChange(index, 'label', v)
            }
            size="small"
            variant="outlined"
            fullWidth
            className={classes.labelField}
          />
          <BlurField
            label="Value"
            value={detail.value || ''}
            onCommit={(v) =>
              handleDetailChange(index, 'value', v)
            }
            size="small"
            variant="outlined"
            fullWidth
            className={classes.valueField}
          />
          <IconButton size="small" onClick={() => handleRemoveDetail(index)}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Button
        startIcon={<AddCircleOutline />}
        onClick={handleAddDetail}
        size="small"
        color="primary"
        className={classes.addButton}
      >
        Add Detail
      </Button>

      <BlurField
        label="Additional Notes"
        value={data.additionalNotes || ''}
        onCommit={(v) => onChange('additionalNotes', v)}
        size="small"
        variant="outlined"
        fullWidth
        multiline
        rows={3}
      />

      <Typography variant="subtitle2" style={{ marginTop: 8 }}>Signature Block</Typography>

      <Box style={{ display: 'flex', gap: 12 }}>
        <BlurField
          label="Closing"
          value={data.signatureClosing || ''}
          onCommit={(v) => onChange('signatureClosing', v)}
          size="small"
          variant="outlined"
          fullWidth
          placeholder="Sincerely,"
        />
        <BlurField
          label="Signer Name"
          value={data.signatureName || ''}
          onCommit={(v) => onChange('signatureName', v)}
          size="small"
          variant="outlined"
          fullWidth
          placeholder="Jules Denton"
        />
      </Box>

      <Box className={classes.crmGroup}>
        <Typography variant="caption" className={classes.crmLabel}>
          Client Info (pre-filled from CRM — editable)
        </Typography>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <BlurField
            label="Client Address"
            value={crmSnapshot?.clientAddress || ''}
            onCommit={(v) => onCrmChange('clientAddress', v)}
            size="small"
            variant="outlined"
            fullWidth
          />
          <BlurField
            label="Contract Term"
            value={crmSnapshot?.term || ''}
            onCommit={(v) => onCrmChange('term', v)}
            size="small"
            variant="outlined"
            fullWidth
            placeholder="e.g. 12 Months"
          />
        </Box>
      </Box>
    </Box>
  )
}

export default ProjectEditor
